import {
  discoveryApiRef,
  fetchApiRef,
  githubAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import Alert from '@material-ui/lab/Alert';
import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { JiraProjectOption } from './jiraBoardTypes';

export type { JiraProjectOption } from './jiraBoardTypes';

type UpdateBoardMappingDialogProps = {
  open: boolean;
  onClose: () => void;
  entityRef: string;
  componentName: string;
  currentProjectKeys: string[];
  staleProjectKeys: string[];
  jiraProjects: JiraProjectOption[];
};

type JobDetails = {
  id: string;
  title: string;
};

export function UpdateBoardMappingDialog({
  open,
  onClose,
  entityRef,
  componentName,
  currentProjectKeys,
  staleProjectKeys,
  jiraProjects,
}: UpdateBoardMappingDialogProps) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const githubAuth = useApi(githubAuthApiRef);

  const [projectKey, setProjectKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [queuedJob, setQueuedJob] = useState<JobDetails | undefined>();

  const sortedProjects = useMemo(
    () => [...jiraProjects].sort((a, b) => a.key.localeCompare(b.key)),
    [jiraProjects],
  );

  const resetState = useCallback(() => {
    setProjectKey('');
    setSubmitting(false);
    setError(undefined);
    setQueuedJob(undefined);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleSubmit = useCallback(async () => {
    if (!projectKey) {
      setError('Select a Jira project');
      return;
    }

    setSubmitting(true);
    setError(undefined);
    try {
      const [batchEditorBaseUrl, githubToken] = await Promise.all([
        discoveryApi.getBaseUrl('batch-editor'),
        githubAuth.getAccessToken(['repo', 'read:user', 'user:email']),
      ]);

      const response = await fetchApi.fetch(`${batchEditorBaseUrl}/jobs/jira-board-mapping`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-GitHub-Authorization': `Bearer ${githubToken}`,
        },
        body: JSON.stringify({
          entityRef,
          projectKey,
          ...(staleProjectKeys.length > 0 ? { removeProjectKeys: staleProjectKeys } : {}),
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `Request failed with ${response.status}`);
      }

      const payload = (await response.json()) as { job: JobDetails };
      setQueuedJob(payload.job);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : String(submitError));
    } finally {
      setSubmitting(false);
    }
  }, [discoveryApi, entityRef, fetchApi, githubAuth, projectKey, staleProjectKeys]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Jira board mapping</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Queue a draft PR that updates <strong>{componentName}</strong> catalog-info.yaml. Review
          and merge it from the Batch Editor.
        </Typography>

        {error ? (
          <Box style={{ marginBottom: 16 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}

        {queuedJob ? (
          <Alert severity="success">
            Draft PR job queued ({queuedJob.id}). Open{' '}
            <Link component={RouterLink} to={`/batch-editor?jobId=${encodeURIComponent(queuedJob.id)}`}>
              Batch Editor
            </Link>{' '}
            to review progress and merge when ready.
          </Alert>
        ) : (
          <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={12}>
            <Box flex="1 1 160px" minWidth={0}>
              <Typography variant="caption" color="textSecondary" display="block">
                Current mapping
              </Typography>
              {currentProjectKeys.length > 0 ? (
                <Box display="flex" flexWrap="wrap" gridGap={4} style={{ marginTop: 4 }}>
                  {currentProjectKeys.map(key => (
                    <Chip key={key} label={key} size="small" variant="outlined" />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                  None
                </Typography>
              )}
            </Box>

            <ArrowForwardIcon color="action" />

            <FormControl variant="outlined" size="small" style={{ flex: '1 1 200px', minWidth: 200 }}>
              <InputLabel id="jira-project-select-label">New Jira project</InputLabel>
              <Select
                labelId="jira-project-select-label"
                label="New Jira project"
                value={projectKey}
                onChange={event => setProjectKey(event.target.value as string)}
              >
                {sortedProjects.map(project => (
                  <MenuItem key={project.key} value={project.key}>
                    {project.key} — {project.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="default">
          {queuedJob ? 'Close' : 'Cancel'}
        </Button>
        {!queuedJob ? (
          <Button
            onClick={() => void handleSubmit()}
            color="primary"
            variant="contained"
            disabled={submitting || sortedProjects.length === 0}
          >
            {submitting ? 'Queueing…' : 'Queue draft PR'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
